import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useState } from "react";
import { IoCheckmark, IoMailOutline } from "react-icons/io5";
import { FaCircleNotch } from "react-icons/fa";
import ReactSelect from "../../../components/common/ReactSelect";
import Toaster from "./../../../components/common/Toaster";
import accountService from "./../../../services/accountService";

const schema = yup.object({
  city: yup.object().shape({
    label: yup.string().required("Please select city."),
    value: yup.string().required("Please select city."),
  }),
  appointees: yup
    .array()
    .min(1, "Please select at least 1 appointee.")
    .required("Please select at least 1 appointee.")
    .nullable(),
});

const SPAForm = ({ data, isLoading, setLoading, onSuccess }) => {
  const [cities, setCities] = useState([]);
  const [appointees, setAppointees] = useState([]);

  const {
    register,
    handleSubmit,
    control,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (info) => {
    try {
      setLoading(true);

      const active_appointees = info.appointees.filter((i) => i.is_active);
      if (active_appointees.length === 0)
        throw new Error("No selected appointees");

      const selected = active_appointees.map((item) => {
        return `${item.first_name} ${item.last_name}`;
      });

      const item = { ...info };
      item["city"] = info.city.value;
      item["appointees"] = selected;

      await accountService.generateSPAAsync(data.id, item);
      Toaster.Success("SPA Sent Successfully!");

      setLoading(false);
      onSuccess();
    } catch (ex) {
      if (ex.message === "No selected appointees") {
        Toaster.Error("Please select at least 1 appointee.");
        setLoading(false);
        return;
      }

      Toaster.Error("Something went wrong.");
      setLoading(false);
    }
  };

  const handleChange = (item) => {
    const data = [...appointees];
    const index = data.indexOf(item);

    data[index] = { ...data[index] };
    data[index].is_active = !data[index].is_active;
    setAppointees(data);

    const selected = data.filter((item) => item.is_active);

    if (selected.length > 0) clearErrors("appointees");

    setValue("appointees", selected);
  };

  const fetchAppointeesAsync = useCallback(
    async (mounted) => {
      try {
        const result = await accountService.getAppointeesAsync();
        if (mounted) {
          const data = result.map((item) => {
            const container = { ...item };

            if (
              item.first_name === "Mark Jandel" ||
              item.first_name === "Axl Gian" ||
              item.first_name === "Robenson" ||
              item.first_name === "Rico" ||
              item.first_name === "Mark Diether" ||
              item.first_name === "Arnel" ||
              item.first_name === "Mark Angelo" ||
              item.first_name === "Joshua Kenneth" ||
              item.first_name === "Emmanuel"
            ) {
              container["is_active"] = true;
            }

            return container;
          });

          setAppointees(data);
          setValue("appointees", data);
        }
      } catch (ex) {
        Toaster.Error("Something went wrong.");
      }
    },
    [setValue]
  );

  useEffect(() => {
    let mounted = true;

    fetchAppointeesAsync(mounted);

    return () => {
      mounted = false;
    };
  }, [fetchAppointeesAsync]);

  useEffect(() => {
    const result = accountService.getSPACities();
    setCities(result);
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <ReactSelect
            id="city-dropdown"
            name="city"
            title="City"
            options={cities}
            control={control}
            register={register("city")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2 mb-2">
          <label className="form-label">Appointees</label>
        </div>
        <div className="col-span-2 space-y-1">
          {appointees.map((item, index) => {
            return (
              <div
                key={index}
                onClick={() => handleChange(item)}
                className="card p-3 cursor-pointer hover:border-blue-400"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-400">
                    {item.first_name} {item.last_name}
                  </p>
                  {item.is_active && <IoCheckmark className="text-green-500" />}
                </div>
              </div>
            );
          })}
          {errors && errors["appointees"] && (
            <div className="pt-2">
              <span className="text-sm text-red-400">
                {errors["appointees"].message}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoMailOutline className="mr-3" />}
            Send SPA via Email
          </button>
        </div>
      </div>
    </form>
  );
};

export default SPAForm;
